/**
 * Subcategory chips (roots-subchips).
 *
 * Chips are plain links and fully functional without this module. It adds:
 * - Active state: the chip whose URL matches the current location gets
 *   `is-active` + `aria-current="true"`.
 * - In-place filtering: a chip that points at the *current* collection with a
 *   query string (a storefront filter) swaps the product grid via the theme's
 *   section renderer instead of a full page load. Anything unexpected falls
 *   back to normal navigation.
 */
class RootsSubcategoryChips extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#syncActive();
    this.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('popstate', () => this.#syncActive(), { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get #links() {
    return Array.from(this.querySelectorAll('a[href]'));
  }

  /**
   * A chip is active when its pathname matches the current page and every
   * query param it carries is present in the current URL. Curated chips have
   * no params, so a pathname match alone activates them; filter chips stay
   * active even when extra params (sort, other filters) are also applied.
   */
  #isActive(link, current) {
    const url = new URL(link.href, window.location.origin);
    if (url.pathname !== current.pathname) return false;

    const params = Array.from(url.searchParams.entries());
    return params.every(([key, value]) => current.searchParams.getAll(key).includes(value));
  }

  #syncActive() {
    const current = new URL(window.location.href);

    for (const link of this.#links) {
      const active = this.#isActive(link, current);
      link.classList.toggle('is-active', active);

      if (active) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element && event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;

    const url = new URL(link.href, window.location.origin);

    // Only filter chips on the current collection render in place; curated
    // chips (different pathname) and param-less links navigate normally.
    if (url.pathname !== window.location.pathname || !url.search) return;

    const sectionId = document.querySelector('results-list[section-id]')?.getAttribute('section-id');
    if (!sectionId) return;

    event.preventDefault();
    history.pushState({}, '', url);
    this.#syncActive();

    import('@theme/section-renderer')
      .then(({ sectionRenderer }) => sectionRenderer.renderSection(sectionId))
      .catch(() => window.location.assign(url));
  };
}

if (!customElements.get('roots-subcategory-chips')) {
  customElements.define('roots-subcategory-chips', RootsSubcategoryChips);
}
